import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';

import { BetaTag } from 'ui-components/components/beta-tag';
import { FlexRow } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Tabs, TabList, TabPanel, Tab } from 'ui-components/components/tabs';
import { Text, TextType } from 'ui-components/components/text';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import Activity from './activity-tab';
import BrandedEmailsTab from './branded-emails-tab/';
import { BrandingTab } from './branding-tab';
import StoreUpdateContextProvider from './context';
import { applyDefaultSettings } from './defaultStoreSettings';
import { DeliveryOptionsContext } from './delivery-options-settings/context';
import { useShouldShowBetaTag, useShouldEnableDeliveryOptions } from './delivery-options-settings/hooks';
import DeliveryOptions from './delivery-options-settings/index';
import { ExtendedProductsTab } from './extended-products-tab/extended-products-tab';
import StoreSettings from './general-settings-tab';
import { useNavigateToStoreTab, useValidationErrors } from './helpers/hooks';
import LifecycleBanner from './lifecycle-banner';
import messages from './messages';
import { PackingSlipsTab } from './packing-slips-tab';
import { STORE_REFRESH_STRATEGY_BACKGROUND, IMPORT_MODES } from './store-refresh-strategy-constants';
import Products from './products-tab';
import ReturnsTab from './returns-tab';
import { ShippingServicesTab } from './shipping-services-tab';
import style from './style.module.less';
import TrackingPage from './tracking-page-tab';
import getIsActive from '../../../../components/data-driven-logo/get-is-active';
import getIsBeta from '../../../../components/data-driven-logo/get-is-beta';
import { SimpleModal } from '../../../../components/simple-modal/simple-modal';
import { useEnable24HourShopifyProductSync } from '../../../../feature-flags/hooks/use-enable-24-hour-shopify-product-sync';
import { useEnableFulfillmentRates } from '../../../../feature-flags/hooks/use-enable-fulfillment-rates';
import { useModal } from '../../../../hooks/use-modal';
import { SENDER_EMAILS_PATH } from '../../../../routes/constants';
import Global from '../../../../store/global-selectors';
import { refreshStoreList } from '../../../../store/seller/seller.actions';
import {
  getStore,
  saveStoreSettings,
  getCustomBranding,
  updateCustomBranding as updateCustomBrandingApi,
  getDoesStoreHaveSettings,
  enableBackgroundImport,
  disableBackgroundImport,
} from '../../api/store-api';
import UpdateBrandingContextProvider from '../../components/branding/context';
import { deserializeBooleans, serializeBooleans } from '../../components/branding/helpers/data-format-helper';
import { ValidationTabsErrors } from '../../components/notifications';
import SaveBar from '../../components/save-bar';
import { SettingsPageTitle } from '../../components/settings-page-title/settings-page-title';
import { useIsSenderEmailsEnabledForSeller } from '../../sender-emails/use-is-sender-emails-enabled-for-seller';
import commonStyles from '../../settings-page.module.less';
import { settingsTabs as tabs } from '../constants';

const TABS_WITHOUT_SAVE_BAR = [tabs.shippingServices];

// Helper function to compute import mode from settings
const getImportModeFromSettings = (settings) => {
  if (settings?.storeRefreshStrategy === STORE_REFRESH_STRATEGY_BACKGROUND) return IMPORT_MODES.Background;
  if (settings?.autoRefresh) return IMPORT_MODES.Automatic;
  return IMPORT_MODES.Manual;
};

const SelectedStore = () => {
  const { storeGuid, storeTab } = useParams();
  const dispatch = useDispatch();

  const [store, setStore] = useState(undefined);
  const [settingsCallbackComplete, setSettingsCallbackComplete] = useState(false);
  const [hasMarketplaceSettings, setHasMarketplaceSettings] = useState(false);
  const [initialName, setInitialName] = useState(undefined);
  const [initialEmail, setInitialEmail] = useState(false);
  const [initialImportMode, setInitialImportMode] = useState(undefined);
  const [customBranding, setCustomBranding] = useState(undefined);

  const userIsSysAdmin = useSelector(Global.selectUserisSysAdmin);
  const marketplacesReference = useSelector(Global.selectReferenceDatagetMarketplaces);

  const isSenderEmailsEnabled = useIsSenderEmailsEnabledForSeller();

  const selectStoreSettings = useNavigateToStoreTab(tabs.storeSettings);
  const selectDeliveryOptions = useNavigateToStoreTab(tabs.checkoutRates);
  const selectBranding = useNavigateToStoreTab(tabs.branding);
  const selectTrackingPage = useNavigateToStoreTab(tabs.trackingPage);
  const selectReturns = useNavigateToStoreTab(tabs.returns);
  const selectBrandedEmails = useNavigateToStoreTab(tabs.brandedEmails);
  const selectPackingSlips = useNavigateToStoreTab(tabs.packingSlips);
  const selectProducts = useNavigateToStoreTab(tabs.products);
  const selectShippingServices = useNavigateToStoreTab(tabs.shippingServices);
  const selectActivity = useNavigateToStoreTab(tabs.activity);
  const shouldShowDeliveryOptions = useShouldEnableDeliveryOptions(store?.marketplaceId);
  const showBetaDeliveryOptionsTag = useShouldShowBetaTag();
  const { isDeliveryMarketplaceAuctaneFulfillment } = useContext(DeliveryOptionsContext);
  const fulfillmentRatesEnabled = useEnableFulfillmentRates();
  const isProductSyncEnabled = useEnable24HourShopifyProductSync();

  const { isOpen: isEmailChangedModalOpen, open: openEmailChangedModal, close: closeEmailChangedModal } = useModal();

  const selectedTab = storeTab ?? tabs.storeSettings;
  const shouldHideSaveBar = TABS_WITHOUT_SAVE_BAR.includes(selectedTab);

  const matchedOrderSource = useMemo(() => {
    if (!store) {
      return;
    }
    return marketplacesReference.find(({ marketplaceId }) => marketplaceId === store.marketplaceId);
  }, [marketplacesReference, store]);

  const isBYOLMarketplace = matchedOrderSource?.bringYourOwnLabelCapable ?? false;
  const shouldShowReturnsTab = !isBYOLMarketplace; // TODO: detect if Enhanced Returns are enabled for this store
  const shouldShowShippingServicesTab = !isBYOLMarketplace;

  const tabsIndexMap = [
    { name: tabs.storeSettings, redirectFn: selectStoreSettings },
    shouldShowDeliveryOptions && {
      name: tabs.checkoutRates,
      redirectFn: selectDeliveryOptions,
    },
    fulfillmentRatesEnabled &&
      isDeliveryMarketplaceAuctaneFulfillment && {
        name: tabs.checkoutRates,
        redirectFn: selectDeliveryOptions,
      },
    { name: tabs.branding, redirectFn: selectBranding },
    { name: tabs.trackingPage, redirectFn: selectTrackingPage },
    shouldShowReturnsTab && { name: tabs.returns, redirectFn: selectReturns },
    { name: tabs.brandedEmails, redirectFn: selectBrandedEmails },
    { name: tabs.packingSlips, redirectFn: selectPackingSlips },
    { name: tabs.products, redirectFn: selectProducts },
    shouldShowShippingServicesTab && {
      name: tabs.shippingServices,
      redirectFn: selectShippingServices,
    },
    { name: tabs.activity, redirectFn: selectActivity },
  ].filter(Boolean);

  const getIndexForTab = useCallback(
    (tabName) => {
      const result = tabsIndexMap.findIndex((tab) => tab.name === tabName);
      return result === -1 ? 0 : result;
    },
    [tabsIndexMap],
  );

  const selectedTabIndex = useMemo(() => getIndexForTab(selectedTab), [getIndexForTab, selectedTab]);

  const manageTabIndexChange = useCallback(
    (index) => {
      tabsIndexMap[index].redirectFn();
    },
    [tabsIndexMap],
  );

  const { errors, updateErrors } = useValidationErrors();
  const isBeta = getIsBeta({ marketplaceId: store?.marketplaceId });
  const isActive = getIsActive({ marketplaceId: store?.marketplaceId });
  const hasBanner = isBeta || !isActive;

  const history = useHistory();
  useEffect(() => {
    if (!storeTab) {
      history.push(`${history.location.pathname}/${tabs.storeSettings}`);
    }
  }, [history, storeTab]);

  useEffect(() => {
    // todo make connections-ui do this backend call. Will come in future verison
    getDoesStoreHaveSettings(storeGuid, userIsSysAdmin)
      .then((result) => {
        setHasMarketplaceSettings(result);
        setSettingsCallbackComplete(true);
      })
      .catch(() => setSettingsCallbackComplete(true));
  }, [storeGuid, setSettingsCallbackComplete, setHasMarketplaceSettings, userIsSysAdmin]);

  const refreshStore = useCallback(() => {
    getStore(storeGuid).then((resp) => {
      // Apply default settings to the store response
      const respWithDefaults = applyDefaultSettings(resp);

      setStore(respWithDefaults);
      const { settings, brandingSettings } = respWithDefaults;
      setInitialName(settings?.storeName);
      setInitialEmail(brandingSettings?.email);
      setInitialImportMode(getImportModeFromSettings(settings));
    });
  }, [storeGuid]);

  useEffect(() => {
    refreshStore();
    getCustomBranding(storeGuid).then((resp) => {
      setCustomBranding(deserializeBooleans(resp));
    });
  }, [storeGuid, refreshStore]);

  const [storeSaveValidationErrors, setStoreSaveValidationErrors] = useState({});

  const resetValidationError = useCallback(
    (keyToReset) => {
      const updatedErrors = { ...storeSaveValidationErrors };
      delete updatedErrors[keyToReset];
      setStoreSaveValidationErrors(updatedErrors);
    },
    [storeSaveValidationErrors],
  );

  const validateTabs = useCallback(() => {
    const tabErrors = [];
    if (!store.packingSlips?.packingSlipId) {
      tabErrors.push(tabs.packingSlips);
      selectPackingSlips();
    }
    return tabErrors;
  }, [store, selectPackingSlips]);

  const saveSettings = async () => {
    try {
      const tabErrors = validateTabs();

      if (tabErrors.length) {
        return Promise.reject(new Error(ValidationTabsErrors[tabErrors[0]]));
      }

      const currentImportMode = getImportModeFromSettings(store.settings);

      // Handle background import toggle
      const isChangingBackgroundImport = initialImportMode === IMPORT_MODES.Background || currentImportMode === IMPORT_MODES.Background;
      if (isChangingBackgroundImport && initialImportMode !== currentImportMode) {
        const updateFunction = currentImportMode === IMPORT_MODES.Background ? enableBackgroundImport : disableBackgroundImport;
        await updateFunction(storeGuid, currentImportMode);
      }
      if (initialImportMode !== currentImportMode) {
        setInitialImportMode(currentImportMode);
      }

      await saveStoreSettings(storeGuid, {
        ...store,
        shouldSkipServiceMappingUpdates: true,
      });

      const newEmail = store.brandingSettings.email;
      const hasChangedEmail = initialEmail !== newEmail;

      if (isSenderEmailsEnabled && hasChangedEmail) {
        setInitialEmail(newEmail);
        openEmailChangedModal();
      }

      await updateCustomBrandingApi(storeGuid, serializeBooleans(customBranding));
      dispatch(refreshStoreList());
    } catch (e) {
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.notificationErrorTitle} />,
        notificationType: NOTIFICATION_TYPE_ERROR,
        content: <FormattedMessage {...messages.notificationErrorContent} />,
      });

      if (e?.response?.data?.Errors) {
        const saveErrors = e.response.data.Errors.reduce((acc, cur) => {
          return {
            ...acc,
            [cur.Message]: cur.Message,
          };
        }, {});
        setStoreSaveValidationErrors(saveErrors);
      }
    }
  };

  const updateTrigger = useCallback(
    (name, value) => {
      const storeSettings = store.settings?.storeSettings || [];
      const triggerExists = storeSettings?.some((setting) => setting.name === name);
      let newStoreSettings = storeSettings;

      if (triggerExists) {
        newStoreSettings = storeSettings.map((setting) => {
          if (setting.name === name) {
            return {
              name,
              value: value.toString(),
            };
          }
          return setting;
        });
      } else {
        newStoreSettings = [
          ...storeSettings,
          {
            name,
            value: value.toString(),
          },
        ];
      }

      setStore((currentStore) => ({
        ...currentStore,
        settings: {
          ...currentStore.settings,
          storeSettings: newStoreSettings,
        },
      }));
    },
    [setStore, store],
  );

  const updateStoreSetting = useCallback(
    (grouping, field, value) => {
      setStore((currentStore) => ({
        ...currentStore,
        [grouping]: {
          ...currentStore[grouping],
          [field]: value,
        },
      }));
    },
    [setStore],
  );

  const updateCustomBranding = useCallback(
    (field, value) => {
      setCustomBranding((currentCustomBranding) => ({
        ...currentCustomBranding,
        [field]: value,
      }));
    },
    [setCustomBranding],
  );

  if (!store || !settingsCallbackComplete) {
    return (
      <div className={style['loading-indicator']}>
        <LoadingIndicator data-testid='selected-store-settings' />
      </div>
    );
  }

  return (
    <StoreUpdateContextProvider
      resetValidationError={resetValidationError}
      storeValidationErrors={storeSaveValidationErrors}
      updateStoreSetting={updateStoreSetting}
      updateTrigger={updateTrigger}
    >
      <UpdateBrandingContextProvider updateCustomBranding={updateCustomBranding} updateErrors={updateErrors}>
        <div className={classnames(commonStyles['content-wrapper'], style['flex-fill-column'])}>
          <div className={classnames(commonStyles['tabbed-content'], style['flex-fill-column'])}>
            <div className={style['flex-fill-column']}>
              <div className={style['selected-store-page-header']}>
                <SettingsPageTitle value={initialName} />
              </div>
              {matchedOrderSource && hasBanner ? <LifecycleBanner marketplaceId={matchedOrderSource.marketplaceId} /> : null}
              <div className={style['tabs-container']}>
                <Tabs
                  onSelectedTabIndexChange={manageTabIndexChange}
                  selectedTabClassName={style['selected-tab']}
                  selectedTabIndex={selectedTabIndex}
                >
                  <TabList className={style['tab-list']}>
                    <Tab>
                      <FormattedMessage {...messages.generalTab} />
                    </Tab>
                    {shouldShowDeliveryOptions ? (
                      <Tab>
                        <FlexRow>
                          <FormattedMessage {...messages.checkoutRatesTab} />
                          {showBetaDeliveryOptionsTag ? <BetaTag /> : null}
                        </FlexRow>
                      </Tab>
                    ) : null}
                    {fulfillmentRatesEnabled && isDeliveryMarketplaceAuctaneFulfillment ? (
                      <Tab>
                        <FormattedMessage {...messages.fulfilmentRatesTab} />
                      </Tab>
                    ) : null}
                    <Tab>
                      <FormattedMessage {...messages.brandingTab} />
                    </Tab>
                    <Tab>
                      <FormattedMessage {...messages.trackingPageTab} />
                    </Tab>
                    {shouldShowReturnsTab ? (
                      <Tab>
                        <FormattedMessage {...messages.returnsTab} />
                      </Tab>
                    ) : null}
                    <Tab>
                      <FormattedMessage {...messages.emailsTab} />
                    </Tab>
                    <Tab>
                      <FormattedMessage {...messages.packingSlipsTab} />
                    </Tab>
                    <Tab>
                      <FormattedMessage {...messages.productsTab} />
                    </Tab>
                    {shouldShowShippingServicesTab ? (
                      <Tab>
                        <FormattedMessage {...messages.shippingServicesTab} />
                      </Tab>
                    ) : null}
                    <Tab>
                      <FormattedMessage {...messages.activityTab} />
                    </Tab>
                  </TabList>

                  <TabPanel selectedClassName={style['selected-tab-panel']}>
                    <StoreSettings
                      hasMarketplaceSettings={hasMarketplaceSettings}
                      marketplaceId={store.marketplaceId}
                      refreshStore={refreshStore}
                      storeGuid={storeGuid}
                      storeSettings={store.settings}
                    />
                  </TabPanel>
                  {shouldShowDeliveryOptions ? (
                    <TabPanel selectedClassName={style['selected-tab-panel']}>
                      <DeliveryOptions />
                    </TabPanel>
                  ) : null}
                  {fulfillmentRatesEnabled && isDeliveryMarketplaceAuctaneFulfillment ? (
                    <TabPanel selectedClassName={style['selected-tab-panel']}>
                      <DeliveryOptions />
                    </TabPanel>
                  ) : null}
                  <TabPanel selectedClassName={style['selected-tab-panel']}>
                    {customBranding ? (
                      <BrandingTab
                        brandingSettings={store.brandingSettings}
                        customBranding={customBranding}
                        emailMessage={store.returns?.emailMessage}
                        returnMessage={store.returns?.returnPolicyMessage}
                        storeGuid={storeGuid}
                      />
                    ) : null}
                  </TabPanel>
                  <TabPanel selectedClassName={style['selected-tab-panel']}>
                    <TrackingPage brandingSettings={store.brandingSettings} />
                  </TabPanel>
                  {shouldShowReturnsTab ? (
                    <TabPanel selectedClassName={style['selected-tab-panel']}>
                      <ReturnsTab returnSettings={store.returns} store={store} />
                    </TabPanel>
                  ) : null}
                  <TabPanel selectedClassName={style['selected-tab-panel']}>
                    <BrandedEmailsTab
                      brandedEmails={store.notifications}
                      brandingSettings={store.brandingSettings}
                      customBranding={customBranding}
                      marketplaceId={store.marketplaceId}
                      returnMessage={store.returns?.returnPolicyMessage}
                      storeSettings={store.settings.storeSettings}
                    />
                  </TabPanel>
                  <TabPanel selectedClassName={style['selected-tab-panel']}>
                    <PackingSlipsTab packingSlips={store.packingSlips} />
                  </TabPanel>
                  <TabPanel selectedClassName={style['selected-tab-panel']}>
                    {isProductSyncEnabled ? (
                      <ExtendedProductsTab
                        matchedOrderSource={matchedOrderSource}
                        products={store.products}
                        storeGuid={storeGuid}
                        storeSettings={store.settings.storeSettings}
                      />
                    ) : (
                      <Products products={store.products} />
                    )}
                  </TabPanel>
                  {shouldShowShippingServicesTab ? (
                    <TabPanel selectedClassName={style['selected-tab-panel']}>
                      <ShippingServicesTab storeGuid={storeGuid} />
                    </TabPanel>
                  ) : null}
                  <TabPanel selectedClassName={style['selected-tab-panel']}>
                    <Activity storeGuid={storeGuid} />
                  </TabPanel>
                </Tabs>
              </div>
            </div>
          </div>
        </div>
        {!shouldHideSaveBar && <SaveBar className={style['save-settings-bar']} disabled={!!errors.length} onSave={saveSettings} />}
        {isEmailChangedModalOpen ? (
          <SimpleModal headerMessage={messages.emailChangedModalHeader} onClose={closeEmailChangedModal}>
            <Text
              formattedMessageValues={{
                a: (chunks) => <Link to={SENDER_EMAILS_PATH}>{chunks}</Link>,
              }}
              textType={TextType.Paragraph}
              value={messages.emailChangedModalText}
            />
          </SimpleModal>
        ) : null}
      </UpdateBrandingContextProvider>
    </StoreUpdateContextProvider>
  );
};

export default SelectedStore;
